/// <amd-module name="Dashboards/WidgetList/DashboardWidgetListPanel" />
import { DashboardPickerItem, SimpleDashboardPicker } from "Dashboards/AddToDashboard/SimpleDashboardPicker";
import { Dashboard, Widget } from "Dashboards/Common/Contracts/Dashboard";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import { IDashboardRestClient } from "Dashboards/Common/RestClient/Dashboard";
import { DashboardOwner, getMyDashboards, getMyTeams } from "Dashboards/DashboardPickerProvider/DashboardPickerProviderFactory";
import { WidgetCatalogInformation, WidgetCatalogQuery } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as Resources from "Dashboards/WidgetList/Resources";
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IButtonProps } from "VSSUI/Button";
import { Panel } from "VSSUI/Panel";
import { IPickListGroup } from "VSSUI/PickList";
import { Spinner } from "VSSUI/Spinner";
import { DashboardWidgetList } from "./DashboardWidgetList";

export interface DashboardWidgetListProps {
    parentPageName: string;
    currentProjectId: string;
    teamContext: WidgetTeamContext;

    onDismiss: () => void;

    /**
     * Returns information about the selected dashboard-widget.
     */
    onOk: (dashboard: Dashboard, widget: Widget) => void;
}

export interface DashboardWidgetListPanelState {
    selectedDashboard: Dashboard | undefined;
    selectedWidget: Widget | undefined;

    dashboards: DashboardPickerItem[] | undefined;
    teamGroups: IPickListGroup[] | undefined;

    currentDashboardWidgets: Widget[] | undefined;
    catalogInformation: WidgetCatalogInformation | undefined;

    panelError: string | undefined;
}

/** This panel is used to allow a user to select a Dashboard from the current project.
 *  Note for testing: The search box only appears if a minimum required number of elements appears.
 */
export class DashboardWidgetListPanel extends VssComponent<DashboardWidgetListProps, DashboardWidgetListPanelState> {
    constructor(props: DashboardWidgetListProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            selectedDashboard: undefined,
            selectedWidget: undefined,

            dashboards: undefined,
            teamGroups: undefined,

            catalogInformation: undefined,

            currentDashboardWidgets: undefined,

            panelError: undefined
        };
    }
    public componentDidMount(): void {
        this.publish("Dashboards.AddToDashboardPanel.Loaded");

        const queryCache = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const query = new WidgetCatalogQuery(this.props.currentProjectId);
        queryCache.getCacheableQueryResult(query).then(catalogInformation => {
            this.setState({ catalogInformation: catalogInformation });
        });

        // Pull the same team and dashboard information used by the dashboard picker, from providers asynchronously.
        getMyDashboards(this.context.pageContext).then((dashboards: Dashboard[]) => {
            this.setState({
                dashboards: dashboards.map(d => {
                    return { ...d, displayName: d.name };
                })
            });
        });

        getMyTeams(this.context.pageContext).then((teams: DashboardOwner[]) => {
            const groups = teams.map(item => {
                return {
                    name: item.name,
                    key: item.id
                } as IPickListGroup;
            });
            this.setState({ teamGroups: groups });
        });
    }

    public render(): JSX.Element | null {
        const footerButtons: IButtonProps[] = [
            {
                className: "widget-config-save",
                onClick: this.onOk,
                text: Resources.PanelOk,
                primary: true,
                disabled: this.state.selectedWidget === undefined
            },
            {
                className: "widget-config-cancel",
                onClick: this.onDismiss,
                text: Resources.PanelCancel
            }
        ];
        const isPickerReady = this.state.dashboards !== undefined && this.state.teamGroups !== undefined;

        return (
            <Panel
                className="widget-list-panel"
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.PanelTitle }}
                footerButtonProps={footerButtons}
            >
                <div className="widget-list-panel-body">
                    {!isPickerReady && <Spinner />}

                    {isPickerReady && (
                        <SimpleDashboardPicker
                            dashboards={this.state.dashboards!}
                            onSelection={this.onSelectedDashboardChanged}
                            selectedItem={
                                this.state.selectedDashboard
                                    ? { ...this.state.selectedDashboard, displayName: this.state.selectedDashboard.name }
                                    : undefined
                            }
                            groups={this.state.teamGroups!}
                        />
                    )}
                    {this.state.selectedDashboard != null && this.state.currentDashboardWidgets != null && (
                        <DashboardWidgetList
                            parentPageName={this.props.parentPageName}
                            currentProjectId={this.props.currentProjectId}
                            selectedItem={this.state.selectedWidget}
                            onDoubleClick={this.onOk}
                            onSelect={this.onSelectWidget}
                            widgets={this.state.currentDashboardWidgets}
                            widgetCatalog={this.state.catalogInformation}
                            teamContext={this.props.teamContext}
                        />
                    )}
                </div>
            </Panel>
        );
        return null;
    }

    private onSelectedDashboardChanged = (dashboard: Dashboard): void => {
        this.setState({ selectedDashboard: dashboard });
        if (dashboard) {
            const client = this.context.pageContext.getRestClient<IDashboardRestClient>("IDashboardRestClient");

            const dashboardId = dashboard.id;
            const projectInfo: TeamContext = {
                project: "",
                projectId: this.props.currentProjectId,
                team: "",
                teamId: dashboard.ownerId
            };
            client.getWidgets(projectInfo, dashboardId, dashboard.eTag).then(widgets => {
                this.setState({ currentDashboardWidgets: widgets.widgets });
            });
        }
    };

    private onSelectWidget = (widget: Widget): void => {
        this.setState({ selectedWidget: widget });
    };

    private onOk = () => {
        this.props.onOk(this.state.selectedDashboard!, this.state.selectedWidget!);
        this.publish("Dashboards.WidgetList.Ok");
    };

    private onDismiss = () => {
        this.props.onDismiss();
        this.publish("Dashboards.WidgetList.Dismiss");
    };

    private publish(featureName: string) {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Dashboards", featureName, {});
    }
}
